import { forwardRef, useId } from 'react';

import { cn } from '@/lib/utils';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface CustomInputProps extends React.ComponentProps<'input'> {
  label?: string;
  placeholder?: string;
  error?: string;
}

export const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
  ({ label, placeholder, error, className, id, ...props }, ref) => {
    // Generate a stable id so the Label binds to the Input via htmlFor.
    // WCAG 1.3.1 — without this, clicking the label doesn't focus the
    // input, and screen readers don't announce the label when the input
    // is focused. Caller-provided `id` wins (rare but supported).
    const generatedId = useId();
    const inputId = id ?? generatedId;
    return (
      <div>
        {label && (
          <Label
            htmlFor={inputId}
            className="block text-sm font-medium text-foreground mb-1"
          >
            {label}
          </Label>
        )}
        <Input
          ref={ref}
          id={inputId}
          placeholder={placeholder}
          className={cn(
            error && 'border-destructive focus-visible:ring-destructive',
            className,
          )}
          aria-invalid={!!error}
          {...props}
        />
        {/* Reserved space for error message */}
        <div className="h-5">
          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>
      </div>
    );
  },
);

CustomInput.displayName = 'CustomInput';
